import React, { useEffect, type FC, type ReactNode } from 'react'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/form'
import { Input } from '../components/input'
import { Button } from '../components/button'

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { detectInputType } from '../lib/detectInputType'
import { detectCountryCode } from '../lib/detectCountryCode'
import { input as formatPhoneInput } from '@munesoft/phonex'
import { countries } from '../lib/CountryCodes'

import CountrySelect from './CountrySelect'
import ErrorComponent from './ErrorComponent'
import { LoaderCircle } from 'lucide-react'
import axios from 'axios'


const loginFormSchema = z.object({
    identity: z.string().min(2, {
        message: "This field is required",
    }).max(255, "Input cannot exceed 255 characters."),
    countryCode: z.string().optional(),
})

type LoginFormsType = {
    children?: ReactNode,
    onSubmit: (values: z.infer<typeof loginFormSchema>) => void,
    isError?: boolean,
    isLoading?: boolean,
    error?: Error | null,
    inputType: 'email' | 'phone';
    setInputType: React.Dispatch<React.SetStateAction<'email' | 'phone'>>;
}

const LoginForm: FC<LoginFormsType> = ({ onSubmit, isError, isLoading, error, inputType, setInputType }) => {

    const form = useForm<z.infer<typeof loginFormSchema>>({
        resolver: zodResolver(loginFormSchema),
        defaultValues: {
            identity: "",
            countryCode: "+1",
        },
    })
    const identity = form.watch('identity')

    // Auto-detect country code when typing a phone number
    useEffect(() => {
        if (identity && identity.replace(/\D/g, '').length >= 2) {
            const detected = detectCountryCode(identity);
            if (detected) {
                form.setValue('countryCode', detected);
            }
        }
    }, [identity]);

    useEffect(() => {
        const type = detectInputType(identity)
        setInputType(type)
    }, [identity])

    const isAxiosError = axios.isAxiosError(error)

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="identity"
                    render={({ field }) => (
                        <FormItem className='mb-0'>
                            <FormLabel className='text-[#38404D]'>Email or Mobile Phone Number<span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl className='mt-[6px]'>
                                <div className='relative w-full'>
                                    {
                                        inputType === 'phone' && (
                                            <FormField
                                                control={form.control}
                                                name="countryCode"
                                                render={({ field }) => (
                                                    <CountrySelect field={field} />
                                                )}
                                            />
                                        )
                                    }
                                    <Input
                                        placeholder="jamesbaer@example.com"
                                        {...field}
                                        {...form.register('identity', { required: true })}
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            if (inputType === 'phone') {
                                                const isoCode = countries.find((c) => c.dial_code === form.getValues('countryCode'))?.code
                                                field.onChange(formatPhoneInput(raw, { country: isoCode }))
                                            } else {
                                                field.onChange(raw)
                                            }
                                        }}
                                        className={`${inputType === "phone" ? "pl-[100px]" : ""}`}
                                        maxLength={255}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className='mt-[16px]'>
                    {
                        isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : ''} />
                    }
                </div>
                <Button type="submit" className='w-full mt-[32px]' disabled={isLoading} isLoading={isLoading}>
    {'Sign in'}
</Button>
            </form>
        </Form>
    )

}

export { LoginForm, loginFormSchema }